// src/pages/users/userLegalStatus.ts
//
// What the admin user list says about a user's legal documents: where the
// user stands and which documents they still have to deal with. Read from the
// same `users/{uid}.legal` record the gate and the server use, and in the same
// order as the gate reads it (`gateState`), so the list never calls a user
// "up to date" whom the gate stops.
import { formatLegalDate } from '../../legal/legalFormat';
import {
    isLegalComplete,
    outstandingDocuments,
    toMillis,
    type LegalDocumentEntry,
    type LegalState,
} from '../../legal/legalState';

export type UserLegalState = 'ok' | 'announced' | 'due' | 'blocked' | 'unset';

export interface UserLegalStatus {
    state: UserLegalState;
    label: string;
    /** What goes with the state: a date, or how it ends. Null for none. */
    detail: string | null;
    /** Titles of the documents the user still has to accept or be shown. */
    documents: string[];
}

// `documents` comes from config/legal and `accepted` from a user record.
// Neither may take the list down by being something else than expected;
// outstandingDocuments skips what is not a document.
function titlesOf(
    documents: LegalDocumentEntry[] | null | undefined,
    accepted: LegalState['accepted'] | null | undefined
): string[] {
    return outstandingDocuments(documents, accepted ?? null).map(
        ({ document }) => {
            const title: unknown = document.title?.en;
            return typeof title === 'string' && title ? title : document.id;
        }
    );
}

export function userLegalStatus(
    legal: LegalState | null | undefined,
    documents: LegalDocumentEntry[] | null | undefined,
    nowMs: number
): UserLegalStatus {
    // No state the server can test: a user created by hand, or one the
    // publish run has not reached. The server refuses such a user. The app
    // writes the record when the user next opens it, and lets them in if
    // nothing is open.
    if (!isLegalComplete(legal)) {
        // Incomplete, but what the user accepted may be there.
        const accepted = (legal as LegalState | null | undefined)?.accepted;
        return {
            state: 'unset',
            label: 'Blocked until next login',
            detail: 'the record is set when the user opens the app',
            documents: titlesOf(documents, accepted),
        };
    }
    // A record from before `due_at` existed has only `valid_until`.
    const dueAt = legal.due_at ?? legal.valid_until;
    // Null first: nothing is open. Only then the time; one that cannot be
    // read counts as passed, as it does for the gate.
    if (dueAt === null) {
        return {
            state: 'ok',
            label: 'Up to date',
            detail: null,
            documents: [],
        };
    }
    const open = titlesOf(documents, legal.accepted);
    const due = toMillis(dueAt);
    if (due !== null && due > nowMs) {
        return {
            state: 'announced',
            label: 'Announced',
            detail: `due ${formatLegalDate(due)}`,
            documents: open,
        };
    }
    // From `valid_until` the server refuses; until then the user is only
    // asked when they start a new session. Null here means the server does
    // not refuse at all, whatever `due_at` was meant to say.
    const refusedFrom = toMillis(legal.valid_until);
    if (refusedFrom === null || refusedFrom > nowMs) {
        return {
            state: 'due',
            label: 'Asked at next login',
            detail:
                refusedFrom === null
                    ? null
                    : `blocked from ${formatLegalDate(refusedFrom)}`,
            documents: open,
        };
    }
    return {
        state: 'blocked',
        label: 'Blocked',
        detail: `since ${formatLegalDate(refusedFrom)}`,
        documents: open,
    };
}
